/**
 * Coregum home : comportements des sections cg-home-gammes, cg-home-demarche,
 * cg-home-avis-videos et cg-home-faq.
 * Chargé en module par chaque section concernée (le navigateur ne l'exécute qu'une fois).
 */
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Vidéo muette en boucle, lue seulement quand elle est visible.
 * <cg-inview-video><video muted loop playsinline preload="none">…</video></cg-inview-video>
 */
class CgInviewVideo extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    const video = this.querySelector('video');
    if (!video) return;
    video.muted = true;
    if (reducedMotion.matches) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (video.preload === 'none') video.preload = 'auto';
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        }
      },
      { threshold: 0.35 }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

/**
 * Onglets accessibles (role tablist / tab / tabpanel) avec fond qui suit l'onglet actif.
 * Les boutons portent [data-cg-tab="index"], les panneaux [data-cg-panel="index"],
 * les fonds optionnels [data-cg-bg="index"].
 */
class CgTabs extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
  }

  get #tabs() {
    return /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[data-cg-tab]')));
  }

  /** @param {number} index */
  select(index) {
    for (const tab of this.#tabs) {
      const active = Number(tab.dataset.cgTab) === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    }
    for (const panel of this.querySelectorAll('[data-cg-panel]')) {
      if (panel instanceof HTMLElement) panel.hidden = Number(panel.dataset.cgPanel) !== index;
    }
    for (const bg of this.querySelectorAll('[data-cg-bg]')) {
      if (bg instanceof HTMLElement) bg.classList.toggle('is-active', Number(bg.dataset.cgBg) === index);
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[data-cg-tab]') : null;
    if (!(tab instanceof HTMLElement) || !this.contains(tab)) return;
    this.select(Number(tab.dataset.cgTab));
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const tabs = this.#tabs;
    const current = tabs.indexOf(/** @type {HTMLButtonElement} */ (document.activeElement));
    if (current === -1) return;

    let next = -1;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (current + 1) % tabs.length;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (current - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;
    if (next === -1) return;

    event.preventDefault();
    const tab = tabs[next];
    if (!tab) return;
    tab.focus();
    this.select(Number(tab.dataset.cgTab));
  };
}

/**
 * Défilement horizontal à accroche (scroll-snap) avec flèches.
 * Marque comme active la diapositive la plus proche du point d'accroche (centre ou début).
 * Avec [data-video], ne lit que la vidéo de la diapositive active (muette par défaut, son + pause).
 */
class CgSnapCarousel extends HTMLElement {
  #controller = new AbortController();
  /** @type {IntersectionObserver | undefined} */
  #visibility;
  /** @type {ResizeObserver | undefined} */
  #resize;
  #inView = false;
  #muted = true;
  #paused = false;
  #active = -1;
  #frame = 0;
  #loopCount = 0;
  #loopTimer = 0;
  #lockWidth = 0;
  #lockHeight = 0;

  connectedCallback() {
    const { signal } = this.#controller;
    const track = this.#track;
    if (!track) return;

    this.#paused = reducedMotion.matches;
    this.addEventListener('click', this.#onClick, { signal });
    track.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    // Recalcule à chaque changement de taille, y compris quand un onglet masqué devient visible.
    this.#resize = new ResizeObserver(this.#onScroll);
    this.#resize.observe(track);

    // Défilement infini : une copie des diapositives avant et après les originales
    if (this.hasAttribute('data-loop')) this.#setupLoop();

    // Démarre sur la diapositive demandée (desktop : celle du milieu), ou sur la 1re originale en boucle.
    requestAnimationFrame(() => {
      if (this.#loopCount) {
        this.#jumpTo(this.#slides[this.#loopCount]);
      } else {
        const start = Number(this.dataset.start ?? 0);
        if (start > 0 && this.#mode === 'center') this.goTo(start, 'instant');
      }
      this.#update();
    });

    if (this.hasAttribute('data-video')) {
      this.#visibility = new IntersectionObserver(
        ([entry]) => {
          this.#inView = Boolean(entry?.isIntersecting);
          this.#syncVideos();
        },
        { threshold: 0.25 }
      );
      this.#visibility.observe(this);
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
    this.#visibility?.disconnect();
    this.#resize?.disconnect();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#loopTimer);
  }

  /** Clone les diapositives (une série avant, une après) pour un défilement sans fin. */
  #setupLoop() {
    const track = this.#track;
    const originals = this.#slides;
    if (!track || originals.length < 2 || track.dataset.cgLooped) return;
    track.dataset.cgLooped = 'true';

    /** @param {HTMLElement} slide */
    const makeClone = (slide) => {
      const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
      clone.dataset.cgClone = '';
      clone.classList.remove('is-active');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('a, button').forEach((element) => element.setAttribute('tabindex', '-1'));
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      return clone;
    };

    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();
    originals.forEach((slide) => {
      before.append(makeClone(slide));
      after.append(makeClone(slide));
    });
    track.prepend(before);
    track.append(after);
    this.#loopCount = originals.length;
  }

  /**
   * Place instantanément une diapositive au point d'accroche, sans animation (saut invisible).
   * @param {HTMLElement | undefined} slide
   */
  #jumpTo(slide) {
    const track = this.#track;
    if (!track || !slide) return;
    const paddingStart = parseFloat(getComputedStyle(track).paddingInlineStart) || 0;
    const delta = slide.getBoundingClientRect().left - track.getBoundingClientRect().left - paddingStart;
    track.dataset.cgJumping = '';
    track.scrollTo({ left: track.scrollLeft + delta, behavior: 'instant' });
    requestAnimationFrame(() => requestAnimationFrame(() => delete track.dataset.cgJumping));
  }

  /** En boucle : si la diapositive active est dans une copie, revient sur son original (même position). */
  #normalizeLoop() {
    const n = this.#loopCount;
    if (!n) return;
    this.#update();
    const slides = this.#slides;
    const active = this.#active;
    let target = -1;
    if (active < n) target = active + n;
    else if (active >= n * 2) target = active - n;
    if (target < 0) return;

    const from = slides[active]?.querySelector('video');
    const to = slides[target]?.querySelector('video');
    if (from && to && from.currentTime > 0) {
      try {
        to.currentTime = from.currentTime;
      } catch (error) {
        // vidéo pas encore chargée : elle repartira du début
      }
    }
    this.#jumpTo(slides[target]);
    this.#update();
  }

  get #track() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-cg-track]'));
  }

  get #slides() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-cg-slide]')));
  }

  /** 'center' (accroche au centre) ou 'start' (accroche à gauche, mobile) */
  get #mode() {
    const track = this.#track;
    if (!track) return 'start';
    return getComputedStyle(this.#slides[0] ?? track).scrollSnapAlign.includes('center') ? 'center' : 'start';
  }

  /**
   * @param {number} index
   * @param {ScrollBehavior} [behavior]
   */
  goTo(index, behavior) {
    const track = this.#track;
    const slides = this.#slides;
    const slide = slides[Math.max(0, Math.min(index, slides.length - 1))];
    if (!track || !slide) return;

    const trackBox = track.getBoundingClientRect();
    const slideBox = slide.getBoundingClientRect();
    const paddingStart = parseFloat(getComputedStyle(track).paddingInlineStart) || 0;
    const delta =
      this.#mode === 'center'
        ? slideBox.left + slideBox.width / 2 - (trackBox.left + trackBox.width / 2)
        : slideBox.left - trackBox.left - paddingStart;

    track.scrollBy({ left: delta, behavior: behavior ?? (reducedMotion.matches ? 'instant' : 'smooth') });
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
    if (this.#loopCount) {
      clearTimeout(this.#loopTimer);
      this.#loopTimer = window.setTimeout(() => this.#normalizeLoop(), 160);
    }
  };

  /**
   * Fige la hauteur de la piste sur la plus grande hauteur observée : pendant le passage d'une vidéo
   * à l'autre, l'ancienne rétrécit et la nouvelle grandit, la piste ne doit pas « respirer »
   * (sinon tout ce qui est en dessous remonte puis redescend).
   * @param {HTMLElement} track
   */
  #lockTrackHeight(track) {
    const width = window.innerWidth;
    if (width !== this.#lockWidth) {
      this.#lockWidth = width;
      this.#lockHeight = 0;
      track.style.minHeight = '';
    }
    const height = track.offsetHeight;
    if (height > this.#lockHeight) {
      this.#lockHeight = height;
      track.style.minHeight = `${height}px`;
    }
  }

  #update() {
    const track = this.#track;
    const slides = this.#slides;
    if (!track || slides.length === 0) return;
    this.#lockTrackHeight(track);

    const trackBox = track.getBoundingClientRect();
    const paddingStart = parseFloat(getComputedStyle(track).paddingInlineStart) || 0;
    const anchor = this.#mode === 'center' ? trackBox.left + trackBox.width / 2 : trackBox.left + paddingStart;

    let best = 0;
    let bestDistance = Infinity;
    slides.forEach((slide, index) => {
      const box = slide.getBoundingClientRect();
      const point = this.#mode === 'center' ? box.left + box.width / 2 : box.left;
      const distance = Math.abs(point - anchor);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });

    const maxScroll = track.scrollWidth - track.clientWidth;
    const atStart = track.scrollLeft <= 2;
    const atEnd = track.scrollLeft >= maxScroll - 2;
    // En fin de piste seulement si la piste défile (sinon, toutes visibles : la première reste active)
    if (this.#mode === 'start' && atEnd && maxScroll > 2) best = slides.length - 1 - this.#trailingVisible(slides, trackBox);

    const prev = this.querySelector('[data-cg-prev]');
    const next = this.querySelector('[data-cg-next]');
    if (prev instanceof HTMLButtonElement) prev.disabled = this.#mode === 'center' ? best === 0 : atStart;
    if (next instanceof HTMLButtonElement) next.disabled = this.#mode === 'center' ? best === slides.length - 1 : atEnd;

    if (best === this.#active) return;
    this.#active = best;
    slides.forEach((slide, index) => {
      slide.classList.toggle('is-active', index === best);
      slide.setAttribute('aria-current', index === best ? 'true' : 'false');
    });
    this.#syncVideos();
  }

  /**
   * Nombre de diapositives entièrement visibles après la première, en fin de piste (mode start).
   * @param {HTMLElement[]} slides
   * @param {DOMRect} trackBox
   */
  #trailingVisible(slides, trackBox) {
    let count = 0;
    for (let i = slides.length - 1; i > 0; i--) {
      const slide = slides[i];
      if (!slide) break;
      const box = slide.getBoundingClientRect();
      if (box.left >= trackBox.left && box.right <= trackBox.right + 1) count++;
      else break;
    }
    return Math.max(0, count - 1);
  }

  #syncVideos() {
    if (!this.hasAttribute('data-video')) return;
    this.#slides.forEach((slide, index) => {
      const video = slide.querySelector('video');
      if (!video) return;
      const active = index === this.#active;
      video.muted = this.#muted;
      if (active && this.#inView && !this.#paused) {
        if (video.preload === 'none') video.preload = 'auto';
        video.play().catch(() => {});
      } else {
        video.pause();
        if (!active) video.muted = true;
      }
      for (const button of slide.querySelectorAll('[data-cg-sound]')) {
        button.setAttribute('aria-pressed', String(active && !this.#muted));
      }
      for (const button of slide.querySelectorAll('[data-cg-playpause]')) {
        button.setAttribute('aria-pressed', String(active && this.#paused));
      }
    });
  }

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-cg-prev]')) return this.#step(-1);
    if (target.closest('[data-cg-next]')) return this.#step(1);

    const slide = /** @type {HTMLElement | null} */ (target.closest('[data-cg-slide]'));
    const index = slide ? this.#slides.indexOf(slide) : -1;

    if (target.closest('[data-cg-sound]')) {
      if (index !== this.#active) this.goTo(index);
      this.#muted = !this.#muted;
      this.#paused = false;
      return this.#syncVideos();
    }

    if (target.closest('[data-cg-playpause]')) {
      if (index !== this.#active) {
        this.goTo(index);
        this.#paused = false;
      } else {
        this.#paused = !this.#paused;
      }
      return this.#syncVideos();
    }

    // Clic sur une vidéo voisine : on la centre.
    if (target.closest('[data-cg-media]') && index !== -1 && index !== this.#active) {
      event.preventDefault();
      this.goTo(index);
      return;
    }

    const add = target.closest('[data-cg-add]');
    if (add instanceof HTMLButtonElement) {
      event.preventDefault();
      await addToCart(add);
    }
  };

  /** @param {number} direction */
  #step(direction) {
    const track = this.#track;
    if (!track) return;
    if (this.#mode === 'center') {
      this.goTo(this.#active + direction);
    } else {
      const slide = this.#slides[0];
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const width = (slide?.getBoundingClientRect().width ?? track.clientWidth) + gap;
      track.scrollBy({ left: direction * width, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
    }
  }
}

/**
 * Ajoute une variante au panier et prévient le thème (ouverture du tiroir, compteur).
 * @param {HTMLButtonElement} button - porte data-variant-id
 */
async function addToCart(button) {
  const variantId = button.dataset.variantId;
  if (!variantId || button.disabled) return;

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'), (element) =>
    element instanceof HTMLElement ? element.dataset.sectionId : undefined
  ).filter((id) => typeof id === 'string' && id.length > 0);

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(
      // @ts-ignore
      window.Theme?.routes?.cart_add_url ?? '/cart/add.js',
      fetchConfig('json', {
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      })
    );
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'Ajout impossible');

    let itemCount = null;
    for (const id of sectionIds) {
      const html = data.sections?.[id];
      if (!html) continue;
      const count = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]')?.textContent;
      if (count) {
        itemCount = parseInt(count, 10);
        break;
      }
    }

    button.dispatchEvent(
      new CartAddEvent({}, variantId, {
        source: itemCount === null ? 'product-form-component' : 'cart-upsells',
        itemCount: itemCount ?? 1,
        productId: button.dataset.productId,
        variantId,
        sections: data.sections,
      })
    );
  } catch (error) {
    console.error(error);
    const href = button.dataset.fallbackUrl;
    if (href) window.location.href = href;
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
}

/**
 * Accordéon FAQ : un seul élément ouvert à la fois (repli si l'attribut name de <details> n'est pas géré).
 */
class CgFaq extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    this.addEventListener(
      'toggle',
      (event) => {
        const details = event.target;
        if (!(details instanceof HTMLDetailsElement) || !details.open) return;
        for (const other of this.querySelectorAll('details[open]')) {
          if (other !== details && other instanceof HTMLDetailsElement) other.open = false;
        }
      },
      { capture: true, signal: this.#controller.signal }
    );
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
  }
}

if (!customElements.get('cg-inview-video')) customElements.define('cg-inview-video', CgInviewVideo);
if (!customElements.get('cg-tabs')) customElements.define('cg-tabs', CgTabs);
if (!customElements.get('cg-snap-carousel')) customElements.define('cg-snap-carousel', CgSnapCarousel);
if (!customElements.get('cg-faq')) customElements.define('cg-faq', CgFaq);
